import { Static, Type } from '@sinclair/typebox';
import { CrmConsentScope, CrmLifecycleStage } from './crm-contact';
import { AdminCrmPeopleFiltersSchema } from './crm-people';
import { CrmMessageStatus } from './crm-queue';
import { CrmSessionFollowupKind, CrmSessionKind } from './crm-training';
import { CropDataSchema } from './image';
import { CampaignErrorStatus, ProCampaignStatus, ProCampaignType } from './namespace';
import { PROJECT_CONFIG } from './project_branding/config';
import { clearableOnUpdate } from './typebox-clearable';

// =====================================================================
// CRM — communications (lot 6) : campagnes et newsletter envoyées par Propelsia à ses propres
// leads et clients, séquences automatiques (chacune sur son audience).
// Moteur séparé des campagnes des pros (`pro_campaign`) : seuls les enums de cycle de vie
// (`ProCampaignType`, `ProCampaignStatus`, `CampaignErrorStatus`) et le format `EmailConfig`
// sont communs.
// =====================================================================

/** Listes sur lesquelles une campagne peut partir : liste de diffusion (inscrits non clients) ou newsletter (clients). */
export const CRM_CAMPAIGN_MAILING_LISTS = [CrmConsentScope.PROSPECTION, CrmConsentScope.NEWSLETTER] as const;

/**
 * Expéditeurs des envois de Propelsia, FIXÉS ici (décision d'Alexandre du 06/10) : rien ne se tape
 * dans l'admin. Les campagnes partent toujours au nom de la marque ; chaque séquence choisit le sien
 * dans cette liste. Ajouter une personne = une valeur ici (stockée en texte, sans migration).
 */
export enum CrmCommsSender {
  PROPELSIA = 'PROPELSIA',
  KALTOUMA = 'KALTOUMA',
  JULIE = 'JULIE',
  ALEXANDRE = 'ALEXANDRE',
}

export interface CrmSenderIdentity {
  name: string;
  email: string;
  reply_to: string;
}

/** Boîte commune qui reçoit TOUTES les réponses aux envois du CRM, quel que soit l'expéditeur (Alexandre, 06/10). */
const CRM_REPLY_TO = `hello@${PROJECT_CONFIG.crm_sender_domain}`;

/** Une personne de l'équipe : « Prénom de <marque> » ; les réponses vont à la boîte commune. */
const teamSender = (firstName: string, localPart: string): CrmSenderIdentity => ({
  name: `${firstName} de ${PROJECT_CONFIG.brand_string}`,
  email: `${localPart}@${PROJECT_CONFIG.crm_sender_domain}`,
  reply_to: CRM_REPLY_TO,
});

/**
 * Identité de chaque expéditeur. Le domaine (`PROJECT_CONFIG.crm_sender_domain`) doit être
 * authentifié chez Mailjet (SPF, DKIM), et la boîte commune doit recevoir des emails (MX) : sinon,
 * en DMARC `p=reject`, chaque envoi est rejeté et les réponses reviennent en erreur.
 */
export const CRM_COMMS_SENDERS: Record<CrmCommsSender, CrmSenderIdentity> = {
  [CrmCommsSender.PROPELSIA]: {
    name: PROJECT_CONFIG.brand_string,
    email: CRM_REPLY_TO,
    reply_to: CRM_REPLY_TO,
  },
  [CrmCommsSender.KALTOUMA]: teamSender('Kaltouma', 'kaltouma'),
  [CrmCommsSender.JULIE]: teamSender('Julie', 'julie'),
  [CrmCommsSender.ALEXANDRE]: teamSender('Alexandre', 'alexandre'),
};

/** Expéditeur des campagnes (toujours la marque) et expéditeur par défaut d'une séquence. */
export const CRM_COMMS_SENDER = CRM_COMMS_SENDERS[CrmCommsSender.PROPELSIA];

/** Expéditeur stocké ; une valeur inconnue (retirée de la liste) retombe sur la marque. */
export const toCrmCommsSender = (raw: string | null | undefined): CrmCommsSender =>
  (Object.values(CrmCommsSender) as string[]).includes(raw ?? '') ? (raw as CrmCommsSender) : CrmCommsSender.PROPELSIA;

export const crmSenderIdentity = (raw: string | null | undefined): CrmSenderIdentity =>
  CRM_COMMS_SENDERS[toCrmCommsSender(raw)];

/** Préfixe de stockage des images déposées depuis le constructeur d'email de l'admin. */
export const CRM_MEDIA_KEY_PREFIX = 'propelsia-crm/';

/** Au-delà de ce taux de désinscription, l'écran des statistiques alerte (spec CRM §9). */
export const CRM_CAMPAIGN_UNSUB_ALERT_RATE = 0.01;

export const CRM_SEQUENCE_STEP_MAX_DELAY_DAYS = 365;

/** Heure d'envoi par défaut d'une étape neuve (heure de Paris). */
export const CRM_SEQUENCE_DEFAULT_SEND_HOUR = 10;
/**
 * Plancher des étapes SMS : SMS marketing du lundi au samedi, de 9 h à 19 h, hors jours fériés
 * (même fenêtre que les campagnes SMS). Une étape SMS a donc une heure de 9 h à 18 h, jamais le dimanche.
 */
export const CRM_SEQUENCE_SMS_WINDOW = { start_hour: 9, end_hour: 19 } as const;
/** Jours de la semaine ISO : 1 = lundi … 7 = dimanche. */
export const CRM_SEQUENCE_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export const CRM_CAMPAIGN_SMS_MAX_LENGTH = 320;

/** Pourquoi une personne de l'audience ne reçoit pas l'envoi. Calculé à l'aperçu et relu juste avant l'envoi. */
export enum CrmCampaignExclusionReason {
  /** Pas d'adresse (email ou mobile selon le canal). */
  NO_ADDRESS = 'NO_ADDRESS',
  /** N'appartient pas à la liste visée (cycle de vie ou ajout manuel). */
  NOT_ON_LIST = 'NOT_ON_LIST',
  /** Refus actif dans le registre de contact. */
  REFUSED = 'REFUSED',
  /** Pro en cours d'essai : la séquence d'essai lui écrit déjà. */
  IN_TRIAL = 'IN_TRIAL',
  /** Un rappel téléphonique est programmé ce jour-là. */
  CALLBACK_TODAY = 'CALLBACK_TODAY',
  /** Même adresse déjà retenue pour une autre fiche. */
  DUPLICATE = 'DUPLICATE',
}

/**
 * Quand part une étape de la séquence :
 *  - AFTER_PREVIOUS : `delay_days` jours après l'étape précédente, à son heure ;
 *  - WEEKDAY : le premier `weekday` à son heure qui suit l'étape précédente. Trois étapes
 *    « lundi 8 h » = trois lundis de suite.
 * Pour la 1re étape, « l'étape précédente » est le point de départ (inscription ou fin d'essai).
 */
export enum CrmSequenceStepTiming {
  AFTER_PREVIOUS = 'AFTER_PREVIOUS',
  WEEKDAY = 'WEEKDAY',
}

/**
 * Point de départ de la 1re étape d'une séquence : l'inscription, la fin de l'essai, ou l'entrée
 * de la personne dans l'audience de la séquence (relevée par la passe horaire ; c'est le seul
 * point de départ d'un lead sans compte, qui n'a ni inscription ni essai).
 */
export enum CrmSequenceAnchor {
  SIGNUP = 'SIGNUP',
  TRIAL_ENDED = 'TRIAL_ENDED',
  AUDIENCE_ENTRY = 'AUDIENCE_ENTRY',
}

/** Origine d'une ligne de l'historique des communications d'une fiche. */
export enum CrmCommsTraceKind {
  CAMPAIGN = 'CAMPAIGN',
  SEQUENCE = 'SEQUENCE',
  /** Email de suite après une séance (démo, formation). */
  SESSION_FOLLOWUP = 'SESSION_FOLLOWUP',
}

/** Variables par destinataire proposées dans le constructeur (en plus de `customer:first_name` / `customer:last_name`). */
export const CRM_CUSTOMER_BINDING_KEYS = [
  'customer:first_name',
  'customer:last_name',
  'customer:business_name',
  'customer:city',
] as const;
export type CrmCustomerBindingKey = (typeof CRM_CUSTOMER_BINDING_KEYS)[number];

const IsoDateBody = Type.String({ minLength: 10, maxLength: 40 });
const EmailConfigJson = Type.Record(Type.String(), Type.Unknown());

// ---------------------------------------------------------------------
// Audiences (`crm_saved_view`) : une liste + des filtres de la page « Personnes », calculés à l'envoi.
// ---------------------------------------------------------------------

export const AdminCrmAudienceFiltersSchema = Type.Omit(AdminCrmPeopleFiltersSchema, ['sort', 'page', 'per_page']);
export type AdminCrmAudienceFilters = Static<typeof AdminCrmAudienceFiltersSchema>;

export const AdminCrmAudienceCountSchema = Type.Object({
  /** Personnes qui correspondent aux filtres, avant tout garde-fou. */
  matched: Type.Number(),
  /** Personnes qui recevraient l'envoi s'il partait maintenant. */
  recipients: Type.Number(),
  excluded: Type.Array(Type.Object({ reason: Type.Enum(CrmCampaignExclusionReason), count: Type.Number() })),
});
export type AdminCrmAudienceCount = Static<typeof AdminCrmAudienceCountSchema>;

export const AdminCrmAudienceItemSchema = Type.Object({
  crm_saved_view_id: Type.Number(),
  name: Type.String(),
  mailing_list: Type.Enum(CrmConsentScope),
  filters: AdminCrmAudienceFiltersSchema,
  /** Nombre de campagnes non envoyées qui visent cette audience (elle n'est pas supprimable tant qu'il y en a). */
  campaigns_count: Type.Number(),
  /** Nombre de séquences qui visent cette audience (elle n'est pas supprimable tant qu'il y en a). */
  sequences_count: Type.Number(),
  updated_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'updated_at', with_time: true } }),
  ),
});
export type AdminCrmAudienceItem = Static<typeof AdminCrmAudienceItemSchema>;

export const AdminCrmAudienceListResponseSchema = Type.Object({ items: Type.Array(AdminCrmAudienceItemSchema) });
export type AdminCrmAudienceListResponse = Static<typeof AdminCrmAudienceListResponseSchema>;

export const AdminCrmAudienceBodySchema = Type.Object({
  name: Type.String({ minLength: 1, maxLength: 120 }),
  mailing_list: Type.Enum(CrmConsentScope),
  filters: AdminCrmAudienceFiltersSchema,
});
export type AdminCrmAudienceBody = Static<typeof AdminCrmAudienceBodySchema>;

export const AdminCrmAudienceCountBodySchema = Type.Object({
  type: Type.Enum(ProCampaignType),
  mailing_list: Type.Enum(CrmConsentScope),
  filters: Type.Optional(AdminCrmAudienceFiltersSchema),
});
export type AdminCrmAudienceCountBody = Static<typeof AdminCrmAudienceCountBodySchema>;

// ---------------------------------------------------------------------
// Campagnes
// ---------------------------------------------------------------------

export const AdminCrmCampaignStatsSchema = Type.Object({
  recipients: Type.Number(),
  delivered: Type.Number(),
  opened: Type.Number(),
  clicked: Type.Number(),
  unsubscribed: Type.Number(),
  bounced: Type.Number(),
  failed: Type.Number(),
  /** Vrai quand le taux de désinscription dépasse `CRM_CAMPAIGN_UNSUB_ALERT_RATE`. */
  unsub_alert: Type.Boolean(),
});
export type AdminCrmCampaignStats = Static<typeof AdminCrmCampaignStatsSchema>;

export const AdminCrmCampaignItemSchema = Type.Object({
  crm_campaign_id: Type.Number(),
  name: Type.String(),
  type: Type.Enum(ProCampaignType),
  status: Type.Enum(ProCampaignStatus),
  error_status: Type.Optional(Type.Enum(CampaignErrorStatus)),
  mailing_list: Type.Enum(CrmConsentScope),
  crm_saved_view_id: Type.Optional(Type.Number()),
  audience_name: Type.Optional(Type.String()),
  subject: Type.Optional(Type.String()),
  nb_recipient: Type.Optional(Type.Number()),
  scheduled_at: Type.Optional(Type.String()),
  scheduled_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'scheduled_at', with_time: true } }),
  ),
  sent_date_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'sent_date', with_time: true } }),
  ),
  updated_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'updated_at', with_time: true } }),
  ),
  stats: Type.Optional(AdminCrmCampaignStatsSchema),
});
export type AdminCrmCampaignItem = Static<typeof AdminCrmCampaignItemSchema>;

export const AdminCrmCampaignDetailSchema = Type.Intersect([
  AdminCrmCampaignItemSchema,
  Type.Object({
    /** Texte du SMS (campagne SMS seulement). */
    content: Type.Optional(Type.String()),
    /** JSON du constructeur (`EmailConfig`), campagne email seulement. */
    email_config: Type.Optional(EmailConfigJson),
  }),
]);
export type AdminCrmCampaignDetail = Static<typeof AdminCrmCampaignDetailSchema>;

export const AdminCrmCampaignListResponseSchema = Type.Object({
  items: Type.Array(AdminCrmCampaignItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});
export type AdminCrmCampaignListResponse = Static<typeof AdminCrmCampaignListResponseSchema>;

export const AdminCrmCampaignCreateBodySchema = Type.Object({
  name: Type.String({ minLength: 1, maxLength: 255 }),
  type: Type.Enum(ProCampaignType),
  mailing_list: Type.Enum(CrmConsentScope),
});
export type AdminCrmCampaignCreateBody = Static<typeof AdminCrmCampaignCreateBodySchema>;

/** Modifiable tant que la campagne est en brouillon. `null` efface le champ. */
export const AdminCrmCampaignUpdateBodySchema = Type.Object({
  name: Type.Optional(Type.String({ minLength: 1, maxLength: 255 })),
  mailing_list: Type.Optional(Type.Enum(CrmConsentScope)),
  crm_saved_view_id: clearableOnUpdate(Type.Number()),
  subject: clearableOnUpdate(Type.String({ maxLength: 255 })),
  content: clearableOnUpdate(Type.String({ maxLength: CRM_CAMPAIGN_SMS_MAX_LENGTH })),
  email_config: Type.Optional(EmailConfigJson),
});
export type AdminCrmCampaignUpdateBody = Static<typeof AdminCrmCampaignUpdateBodySchema>;

export const AdminCrmCampaignScheduleBodySchema = Type.Object({
  /** Date ISO (UTC). Chaîne simple : pas de conversion de fuseau par `@TypeBoxBody`. */
  scheduled_at: IsoDateBody,
});
export type AdminCrmCampaignScheduleBody = Static<typeof AdminCrmCampaignScheduleBodySchema>;

export const AdminCrmEmailPreviewResponseSchema = Type.Object({
  subject: Type.String(),
  html: Type.String(),
});
export type AdminCrmEmailPreviewResponse = Static<typeof AdminCrmEmailPreviewResponseSchema>;

export const AdminCrmSendTestResponseSchema = Type.Object({
  /** Adresse (ou numéro) de l'admin connecté qui a reçu le test. */
  sent_to: Type.String(),
});
export type AdminCrmSendTestResponse = Static<typeof AdminCrmSendTestResponseSchema>;

export const AdminCrmCampaignRecipientItemSchema = Type.Object({
  crm_campaign_recipient_id: Type.Number(),
  lead_id: Type.Optional(Type.Number()),
  professional_id: Type.Optional(Type.Number()),
  label: Type.String(),
  address: Type.Optional(Type.String()),
  status: Type.Enum(CrmMessageStatus),
  sent_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'sent_at', with_time: true } }),
  ),
  opened_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'opened_at', with_time: true } }),
  ),
  clicked_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'clicked_at', with_time: true } }),
  ),
  unsubscribed_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'unsubscribed_at', with_time: true } }),
  ),
  bounced_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'bounced_at', with_time: true } }),
  ),
});
export type AdminCrmCampaignRecipientItem = Static<typeof AdminCrmCampaignRecipientItemSchema>;

export const AdminCrmCampaignRecipientsResponseSchema = Type.Object({
  items: Type.Array(AdminCrmCampaignRecipientItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});
export type AdminCrmCampaignRecipientsResponse = Static<typeof AdminCrmCampaignRecipientsResponseSchema>;

// ---------------------------------------------------------------------
// Séquences automatiques, réglées dans l'admin. Chacune vise une audience : une liste (diffusion =
// inscrits non clients, newsletter = clients), restreinte en option par une audience enregistrée.
// ---------------------------------------------------------------------

export const AdminCrmSequenceStepSchema = Type.Object({
  crm_sequence_step_id: Type.Number(),
  position: Type.Number(),
  timing: Type.Enum(CrmSequenceStepTiming),
  /** Jours après l'étape précédente, ou le point de départ pour la 1re étape (mode AFTER_PREVIOUS). */
  delay_days: Type.Number(),
  /** Jour de la semaine ISO, 1 = lundi … 7 = dimanche (mode WEEKDAY). */
  weekday: Type.Optional(Type.Number()),
  /** Heure d'envoi, heure de Paris, à l'heure pleine. */
  send_hour: Type.Number(),
  /** Canal de l'étape : EMAIL ou SMS. */
  channel: Type.Enum(ProCampaignType),
  /** Objet de l'email, ou nom de l'étape pour un SMS. */
  subject: Type.String(),
  email_config: EmailConfigJson,
  /** Texte du SMS (étape SMS). */
  content: Type.Optional(Type.String()),
  is_active: Type.Boolean(),
  sent: Type.Number(),
  opened: Type.Number(),
  clicked: Type.Number(),
  unsubscribed: Type.Number(),
});
export type AdminCrmSequenceStep = Static<typeof AdminCrmSequenceStepSchema>;

/** Ce qui décrit une séquence dans la liste comme dans le détail. */
const SequenceSummaryFields = {
  crm_sequence_id: Type.Number(),
  name: Type.String(),
  is_active: Type.Boolean(),
  /** Liste visée (celle de l'audience enregistrée quand il y en a une). */
  mailing_list: Type.Enum(CrmConsentScope),
  crm_saved_view_id: Type.Optional(Type.Number()),
  audience_name: Type.Optional(Type.String()),
  anchor: Type.Enum(CrmSequenceAnchor),
  /** Expéditeur des messages de la séquence (identités fixées dans `CRM_COMMS_SENDERS`). */
  sender: Type.Enum(CrmCommsSender),
};

export const AdminCrmSequenceItemSchema = Type.Object({
  ...SequenceSummaryFields,
  /** Étapes actives / toutes les étapes. */
  active_steps: Type.Number(),
  steps_count: Type.Number(),
  /** Messages partis, toutes étapes confondues. */
  sent: Type.Number(),
  updated_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'updated_at', with_time: true } }),
  ),
});
export type AdminCrmSequenceItem = Static<typeof AdminCrmSequenceItemSchema>;

export const AdminCrmSequenceListResponseSchema = Type.Object({ items: Type.Array(AdminCrmSequenceItemSchema) });
export type AdminCrmSequenceListResponse = Static<typeof AdminCrmSequenceListResponseSchema>;

export const AdminCrmSequenceSchema = Type.Object({
  ...SequenceSummaryFields,
  /** Seules les personnes dont le point de départ est postérieur à cette date entrent dans la séquence (ISO). */
  applies_from: Type.Optional(Type.String()),
  applies_from_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'applies_from', with_time: true } }),
  ),
  /** Envoyer aussi le samedi et le dimanche (étapes « X jours après » ; une étape à jour fixe suit son jour). */
  send_on_weekends: Type.Boolean(),
  steps: Type.Array(AdminCrmSequenceStepSchema),
  /**
   * Population (audience recalculée) : renvoyée par la LECTURE seule, pas par les écritures, dont
   * la réponse ne refait pas ce calcul. L'écran garde les chiffres qu'il a, et relit s'ils
   * peuvent avoir changé.
   */
  /** Personnes dans l'audience en ce moment (avant le point de départ et les étapes). */
  members: Type.Optional(Type.Number()),
  /** Personnes actuellement dans la séquence (au moins une étape restante). */
  in_progress: Type.Optional(Type.Number()),
  /**
   * Entrée dans l'audience : personnes de l'audience que la séquence n'a pas encore relevées. À
   * l'activation, elles sont mises de côté, sauf si l'on choisit de leur écrire aussi.
   */
  not_enrolled: Type.Optional(Type.Number()),
});
export type AdminCrmSequence = Static<typeof AdminCrmSequenceSchema>;

export const AdminCrmSequenceCreateBodySchema = Type.Object({
  name: Type.String({ minLength: 1, maxLength: 120 }),
  mailing_list: Type.Enum(CrmConsentScope),
  /** Audience enregistrée : la séquence prend sa liste. */
  crm_saved_view_id: Type.Optional(Type.Number()),
  anchor: Type.Optional(Type.Enum(CrmSequenceAnchor)),
  sender: Type.Optional(Type.Enum(CrmCommsSender)),
});
export type AdminCrmSequenceCreateBody = Static<typeof AdminCrmSequenceCreateBodySchema>;

/** L'audience et le point de départ ne changent que sur une séquence inactive. */
export const AdminCrmSequenceUpdateBodySchema = Type.Object({
  name: Type.Optional(Type.String({ minLength: 1, maxLength: 120 })),
  is_active: Type.Optional(Type.Boolean()),
  /**
   * Avec `is_active: true` et le point de départ « entrée dans l'audience » : écrire aussi aux
   * personnes déjà dans l'audience. Faux par défaut : elles sont mises de côté.
   */
  include_existing: Type.Optional(Type.Boolean()),
  mailing_list: Type.Optional(Type.Enum(CrmConsentScope)),
  crm_saved_view_id: clearableOnUpdate(Type.Number()),
  anchor: Type.Optional(Type.Enum(CrmSequenceAnchor)),
  sender: Type.Optional(Type.Enum(CrmCommsSender)),
  applies_from: Type.Optional(IsoDateBody),
  send_on_weekends: Type.Optional(Type.Boolean()),
});
export type AdminCrmSequenceUpdateBody = Static<typeof AdminCrmSequenceUpdateBodySchema>;

const StepTimingFields = {
  timing: Type.Optional(Type.Enum(CrmSequenceStepTiming)),
  delay_days: Type.Optional(Type.Integer({ minimum: 0, maximum: CRM_SEQUENCE_STEP_MAX_DELAY_DAYS })),
  weekday: Type.Optional(Type.Integer({ minimum: 1, maximum: 7 })),
  send_hour: Type.Optional(Type.Integer({ minimum: 0, maximum: 23 })),
};

export const AdminCrmSequenceStepCreateBodySchema = Type.Object({
  ...StepTimingFields,
  subject: Type.String({ minLength: 1, maxLength: 255 }),
  /** EMAIL par défaut. */
  channel: Type.Optional(Type.Enum(ProCampaignType)),
});
export type AdminCrmSequenceStepCreateBody = Static<typeof AdminCrmSequenceStepCreateBodySchema>;

export const AdminCrmSequenceStepUpdateBodySchema = Type.Object({
  ...StepTimingFields,
  subject: Type.Optional(Type.String({ minLength: 1, maxLength: 255 })),
  email_config: Type.Optional(EmailConfigJson),
  content: Type.Optional(Type.String({ maxLength: CRM_CAMPAIGN_SMS_MAX_LENGTH })),
  is_active: Type.Optional(Type.Boolean()),
});
export type AdminCrmSequenceStepUpdateBody = Static<typeof AdminCrmSequenceStepUpdateBodySchema>;

// ---------------------------------------------------------------------
// Contexte du constructeur, images, trace sur la fiche
// ---------------------------------------------------------------------

/**
 * Même forme que `EmailBindableField` : `label` et `group` sont des CLÉS de traduction (namespace
 * `DYNAMIC_FORM`), `raw_label` le libellé français déjà résolu (il gagne quand il est présent).
 */
const BindableFieldSchema = Type.Object({
  label: Type.String(),
  group: Type.String(),
  type: Type.String(),
  raw_label: Type.Optional(Type.String()),
});

/** Ce que l'admin envoie à l'iframe du constructeur : identité de Propelsia et variables disponibles. */
export const AdminCrmBuilderContextSchema = Type.Object({
  /** `ProfessionalBindingData` construit avec l'identité de Propelsia (résout les variables `pro:*`). */
  binding_data: Type.Record(Type.String(), Type.Unknown()),
  bindable_fields: Type.Record(Type.String(), BindableFieldSchema),
  /** Valeurs d'exemple des variables par destinataire, pour l'aperçu (« Jean », « Dupont »…). */
  preview_values: Type.Record(Type.String(), Type.String()),
  /** `EmailConfig` de départ d'un email neuf. */
  default_config: EmailConfigJson,
});
export type AdminCrmBuilderContext = Static<typeof AdminCrmBuilderContextSchema>;

export const AdminCrmMediaPresignBodySchema = Type.Object({
  filename: Type.String({ minLength: 1, maxLength: 255 }),
  type: Type.String({ minLength: 1, maxLength: 120 }),
});
export type AdminCrmMediaPresignBody = Static<typeof AdminCrmMediaPresignBodySchema>;

export const AdminCrmMediaPresignResponseSchema = Type.Object({
  put_url: Type.String(),
  /** Clé de stockage, à repasser à `media/public-url`. */
  key: Type.String(),
});
export type AdminCrmMediaPresignResponse = Static<typeof AdminCrmMediaPresignResponseSchema>;

export const AdminCrmMediaPublicUrlBodySchema = Type.Object({
  key: Type.String({ minLength: 1, maxLength: 500 }),
  crop: Type.Optional(CropDataSchema),
  sizes: Type.Optional(Type.Array(Type.String({ maxLength: 120 }), { maxItems: 8, uniqueItems: true })),
});
export type AdminCrmMediaPublicUrlBody = Static<typeof AdminCrmMediaPublicUrlBodySchema>;

export const AdminCrmMediaPublicUrlResponseSchema = Type.Object({
  url: Type.String(),
  sized_urls: Type.Optional(Type.Array(Type.Object({ size: Type.String(), url: Type.String() }))),
});
export type AdminCrmMediaPublicUrlResponse = Static<typeof AdminCrmMediaPublicUrlResponseSchema>;

/** Une ligne de l'historique des communications d'une fiche (campagne reçue ou étape de séquence). */
export const AdminCrmCommsTraceItemSchema = Type.Object({
  kind: Type.Enum(CrmCommsTraceKind),
  type: Type.Enum(ProCampaignType),
  /** Nom de la campagne, ou objet de l'étape de séquence. */
  label: Type.String(),
  crm_campaign_id: Type.Optional(Type.Number()),
  /** Séquence de l'étape (ligne de séquence seulement). */
  crm_sequence_id: Type.Optional(Type.Number()),
  sequence_name: Type.Optional(Type.String()),
  status: Type.Enum(CrmMessageStatus),
  sent_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'sent_at', with_time: true } }),
  ),
  opened: Type.Boolean(),
  clicked: Type.Boolean(),
  unsubscribed: Type.Boolean(),
});
export type AdminCrmCommsTraceItem = Static<typeof AdminCrmCommsTraceItemSchema>;

export const AdminCrmCommsTraceResponseSchema = Type.Object({ items: Type.Array(AdminCrmCommsTraceItemSchema) });
export type AdminCrmCommsTraceResponse = Static<typeof AdminCrmCommsTraceResponseSchema>;

// ---------------------------------------------------------------------
// Bibliothèque de modèles d'email : un nom, un objet, un contenu du constructeur. Un modèle se
// COPIE dans une campagne, une étape de séquence ou un email de suite (le modifier ensuite ne
// change pas ce qui a déjà été copié) ; l'inverse existe aussi (« enregistrer comme modèle »).
// ---------------------------------------------------------------------

export const CRM_EMAIL_TEMPLATE_NAME_MAX_LENGTH = 120;
/** Un nom doit porter au moins un caractère visible : des espaces seuls passeraient `minLength`. */
const NON_BLANK = '\\S';

/** Un JSON de constructeur vide ou sans bloc : « pas encore de contenu » (même lecture côté admin et backend). */
export const isEmptyEmailConfig = (config: unknown): boolean => {
  if (config === null || typeof config !== 'object') return true;
  const components = (config as { components?: unknown }).components;
  return !Array.isArray(components) || components.length === 0;
};

export const AdminCrmEmailTemplateItemSchema = Type.Object({
  crm_email_template_id: Type.Number(),
  name: Type.String(),
  subject: Type.String(),
  /** Objet ET contenu présents. */
  has_content: Type.Boolean(),
  updated_by_name: Type.Optional(Type.String()),
  updated_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'updated_at', with_time: true } }),
  ),
});
export type AdminCrmEmailTemplateItem = Static<typeof AdminCrmEmailTemplateItemSchema>;

export const AdminCrmEmailTemplateDetailSchema = Type.Intersect([
  AdminCrmEmailTemplateItemSchema,
  Type.Object({ email_config: Type.Optional(EmailConfigJson) }),
]);
export type AdminCrmEmailTemplateDetail = Static<typeof AdminCrmEmailTemplateDetailSchema>;

export const AdminCrmEmailTemplateListResponseSchema = Type.Object({
  items: Type.Array(AdminCrmEmailTemplateItemSchema),
});
export type AdminCrmEmailTemplateListResponse = Static<typeof AdminCrmEmailTemplateListResponseSchema>;

/** Création : un nom ; l'objet et le contenu peuvent venir d'un email existant (« enregistrer comme modèle »). */
export const AdminCrmEmailTemplateCreateBodySchema = Type.Object({
  name: Type.String({ minLength: 1, maxLength: CRM_EMAIL_TEMPLATE_NAME_MAX_LENGTH, pattern: NON_BLANK }),
  subject: Type.Optional(Type.String({ maxLength: 255 })),
  email_config: Type.Optional(EmailConfigJson),
});
export type AdminCrmEmailTemplateCreateBody = Static<typeof AdminCrmEmailTemplateCreateBodySchema>;

export const AdminCrmEmailTemplateUpdateBodySchema = Type.Object({
  name: Type.Optional(Type.String({ minLength: 1, maxLength: CRM_EMAIL_TEMPLATE_NAME_MAX_LENGTH, pattern: NON_BLANK })),
  subject: Type.Optional(Type.String({ maxLength: 255 })),
  email_config: Type.Optional(EmailConfigJson),
});
export type AdminCrmEmailTemplateUpdateBody = Static<typeof AdminCrmEmailTemplateUpdateBodySchema>;

// ---------------------------------------------------------------------
// Emails de suite de séance (onglet « Emails automatiques » de « Campagnes ») : un emplacement
// par prestation de séance (chaque formation, chaque démo des listes `CRM_*_PRESTATION_IDS`),
// rédigé ici, envoyé par la passe horaire qui suit le pointage d'une présence.
// ---------------------------------------------------------------------

export const AdminCrmSessionFollowupItemSchema = Type.Object({
  kind: Type.Enum(CrmSessionFollowupKind),
  /** Prestation de séance (identifiant des routes de réglage). */
  prestation_id: Type.Number(),
  /** Nom de la prestation dans le catalogue du compte de formation. */
  prestation_name: Type.String(),
  session_kind: Type.Enum(CrmSessionKind),
  subject: Type.String(),
  email_config: Type.Optional(EmailConfigJson),
  sender: Type.Enum(CrmCommsSender),
  is_active: Type.Boolean(),
  /** Objet ET contenu présents : sans eux, l'emplacement ne peut pas être activé. */
  has_content: Type.Boolean(),
  sent_count: Type.Number(),
  last_sent_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_sent_at', with_time: true } }),
  ),
  updated_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'updated_at', with_time: true } }),
  ),
});
export type AdminCrmSessionFollowupItem = Static<typeof AdminCrmSessionFollowupItemSchema>;

export const AdminCrmSessionFollowupListResponseSchema = Type.Object({
  items: Type.Array(AdminCrmSessionFollowupItemSchema),
});
export type AdminCrmSessionFollowupListResponse = Static<typeof AdminCrmSessionFollowupListResponseSchema>;

export const AdminCrmSessionFollowupUpdateBodySchema = Type.Object({
  subject: Type.Optional(Type.String({ maxLength: 255 })),
  email_config: Type.Optional(EmailConfigJson),
  sender: Type.Optional(Type.Enum(CrmCommsSender)),
  is_active: Type.Optional(Type.Boolean()),
});
export type AdminCrmSessionFollowupUpdateBody = Static<typeof AdminCrmSessionFollowupUpdateBodySchema>;

/** Étapes du cycle de vie qui mettent d'office sur une liste, hors pros en essai (exclus de tout envoi marketing). */
export const CRM_CAMPAIGN_LIST_STAGES: Record<(typeof CRM_CAMPAIGN_MAILING_LISTS)[number], CrmLifecycleStage[]> = {
  [CrmConsentScope.PROSPECTION]: [CrmLifecycleStage.TRIAL_ENDED],
  [CrmConsentScope.NEWSLETTER]: [CrmLifecycleStage.CLIENT],
};

// =====================================================================
// Pont `postMessage` entre l'admin (hôte) et le constructeur d'email affiché en iframe
// (`frontend-web`, route `/crm-email-builder`). L'iframe n'appelle aucune API et ne porte aucun
// jeton : tout passe par ces messages, dont l'origine est vérifiée des deux côtés.
// =====================================================================

export const CRM_EMAIL_BUILDER_PATH = '/crm-email-builder';
export const CRM_EMAIL_BUILDER_SOURCE = 'propelsia-crm-email-builder';

export enum CrmEmailBuilderMessageType {
  /** iframe → hôte : montée, attend `INIT`. */
  READY = 'ready',
  /** hôte → iframe : JSON à éditer et contexte. */
  INIT = 'init',
  /** iframe → hôte : le JSON a changé. */
  CHANGE = 'change',
  /** iframe → hôte : demande d'une adresse de dépôt pour un fichier. */
  PRESIGN_REQUEST = 'presign-request',
  PRESIGN_RESPONSE = 'presign-response',
  /** iframe → hôte : demande de l'URL publique signée d'une clé de stockage. */
  SIGN_REQUEST = 'sign-request',
  SIGN_RESPONSE = 'sign-response',
}

interface CrmEmailBuilderEnvelope {
  source: typeof CRM_EMAIL_BUILDER_SOURCE;
}

export type CrmEmailBuilderIframeMessage = CrmEmailBuilderEnvelope &
  (
    | { type: CrmEmailBuilderMessageType.READY }
    | { type: CrmEmailBuilderMessageType.CHANGE; config: Record<string, unknown> }
    | { type: CrmEmailBuilderMessageType.PRESIGN_REQUEST; request_id: string; filename: string; content_type: string }
    | {
        type: CrmEmailBuilderMessageType.SIGN_REQUEST;
        request_id: string;
        key: string;
        crop?: AdminCrmMediaPublicUrlBody['crop'];
        sizes?: string[];
      }
  );

export type CrmEmailBuilderHostMessage = CrmEmailBuilderEnvelope &
  (
    | {
        type: CrmEmailBuilderMessageType.INIT;
        config: Record<string, unknown>;
        context: Omit<AdminCrmBuilderContext, 'default_config'>;
      }
    | {
        type: CrmEmailBuilderMessageType.PRESIGN_RESPONSE;
        request_id: string;
        ok: boolean;
        put_url?: string;
        key?: string;
      }
    | {
        type: CrmEmailBuilderMessageType.SIGN_RESPONSE;
        request_id: string;
        ok: boolean;
        url?: string;
        sized_urls?: { size: string; url: string }[];
      }
  );

const isEnvelope = (data: unknown): data is { source: string; type: string } =>
  typeof data === 'object' &&
  data !== null &&
  (data as { source?: unknown }).source === CRM_EMAIL_BUILDER_SOURCE &&
  typeof (data as { type?: unknown }).type === 'string';

/** Message reçu par l'hôte (admin) : vérifie l'enveloppe, pas l'origine (à contrôler par l'appelant). */
export function parseCrmEmailBuilderIframeMessage(data: unknown): CrmEmailBuilderIframeMessage | null {
  if (!isEnvelope(data)) return null;
  const iframeTypes: string[] = [
    CrmEmailBuilderMessageType.READY,
    CrmEmailBuilderMessageType.CHANGE,
    CrmEmailBuilderMessageType.PRESIGN_REQUEST,
    CrmEmailBuilderMessageType.SIGN_REQUEST,
  ];
  return iframeTypes.includes(data.type) ? (data as CrmEmailBuilderIframeMessage) : null;
}

/** Message reçu par l'iframe : vérifie l'enveloppe, pas l'origine (à contrôler par l'appelant). */
export function parseCrmEmailBuilderHostMessage(data: unknown): CrmEmailBuilderHostMessage | null {
  if (!isEnvelope(data)) return null;
  const hostTypes: string[] = [
    CrmEmailBuilderMessageType.INIT,
    CrmEmailBuilderMessageType.PRESIGN_RESPONSE,
    CrmEmailBuilderMessageType.SIGN_RESPONSE,
  ];
  return hostTypes.includes(data.type) ? (data as CrmEmailBuilderHostMessage) : null;
}
